<script setup lang="ts">
import { FocusTrap, Popover as HeadlessPopover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { onBeforeUnmount, onMounted, onUnmounted, ref } from 'vue'

import type { PopoverProps } from './types'

import { useFloating } from '@/shared/ui/floating'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

const HOVER_CLOSE_DELAY_MS = 150

// House style is Button's: one map per variant, each owning the whole recipe. Colour, radius and
// border-WIDTH therefore live in the branches rather than in the shared base — two Tailwind
// utilities for the same property are emitted at equal specificity, so a base `bg-surface` under a
// variant's `bg-transparent` would be decided by stylesheet order, not by the class list.
const TRIGGER_BASE_CLASS =
  'ui-popover__button cursor-pointer items-center justify-center outline-none focus-visible:shadow-[0_0_0_2px_var(--ui-color-ring)] disabled:cursor-not-allowed disabled:opacity-60'

const TRIGGER_VARIANT_CLASS: Record<NonNullable<PopoverProps['triggerVariant']>, string> = {
  default:
    'min-h-8 rounded-sm border border-border bg-surface px-sm text-fg focus-visible:border-ring',
  plain:
    'ui-popover__button--plain min-h-0 rounded-sm border-0 bg-transparent p-0 text-fg focus-visible:border-transparent',
  // Radius: the recipe measured 0.375rem, which is not a step — `--ui-radius-sm` is 0.25rem and
  // `--ui-radius-md` 0.5rem. `rounded-md` is the one the rest of this filter-toolbar family already
  // uses (`Select` variant="filled", `Button` shape="rounded"), so the chip snaps up to the scale
  // rather than keeping a 1px-faithful literal (contract 02 §10, "Parity with v3").
  chip: 'ui-popover__button--chip min-h-8 rounded-md border px-sm py-1.5 focus-visible:border-ring',
}

// Rest vs open are two branches of the SAME properties for the same reason as above — an `:has()`
// override is what the migrated hosts used, and it beat their own `:hover` rule on source order.
const CHIP_REST_CLASS = 'border-transparent bg-surface-sunken text-muted hover:bg-border'
const CHIP_ACTIVE_CLASS = 'ui-popover__button--active border-primary bg-primary text-primary-fg'

const props = withDefaults(defineProps<PopoverProps>(), {
  placement: 'bottom',
  offset: 8,
  triggerVariant: 'default',
  panelVariant: 'default',
  fluid: false,
  disabled: false,
  openOnHover: false,
})
const popoverFocusFeatures =
  FocusTrap.features.InitialFocus | FocusTrap.features.TabLock | FocusTrap.features.RestoreFocus

function triggerClass(isOpen: boolean): string {
  const variant = TRIGGER_VARIANT_CLASS[props.triggerVariant]
  if (props.triggerVariant !== 'chip') return variant
  return `${variant} ${isOpen ? CHIP_ACTIVE_CLASS : CHIP_REST_CLASS}`
}

const reference = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)
const { floatingStyles } = useFloating(reference, floating, {
  placement: props.placement,
  offset: props.offset,
  strategy: 'fixed',
})

// Independent of Headless's click-driven `open` — legacy's equivalent popovers opened on hover.
const hoverOpen = ref(false)
let hoverCloseTimer: ReturnType<typeof setTimeout> | undefined

function onHoverEnter(): void {
  if (!props.openOnHover) return
  if (hoverCloseTimer) {
    clearTimeout(hoverCloseTimer)
    hoverCloseTimer = undefined
  }
  hoverOpen.value = true
}

function onHoverLeave(): void {
  if (!props.openOnHover) return
  hoverCloseTimer = setTimeout(() => {
    hoverOpen.value = false
    hoverCloseTimer = undefined
  }, HOVER_CLOSE_DELAY_MS)
}

onBeforeUnmount(() => {
  if (hoverCloseTimer) clearTimeout(hoverCloseTimer)
})

function restoreTriggerFocus() {
  window.setTimeout(
    () => reference.value?.querySelector('button')?.focus({ preventScroll: true }),
    0,
  )
}

function closeFromContent(close: () => void): void {
  close()
  restoreTriggerFocus()
}

/** FocusTrap receives every currently-active child layer even when its DOM is portalled beside the
 * parent under `body`. Overlay order is the central nesting truth; product components do not need
 * to know where a nested dropdown/menu happens to paint. */
function descendantFocusContainers(): HTMLElement[] {
  const parentOrder = Number(floating.value?.dataset.overlayOrder ?? -1)
  if (parentOrder < 0) return []
  return [...document.querySelectorAll<HTMLElement>('[data-overlay-order]')].filter(
    (element) => Number(element.dataset.overlayOrder) > parentOrder,
  )
}

function handleEscape(event: KeyboardEvent) {
  const topLayer = [...document.querySelectorAll<HTMLElement>('[data-overlay-order]')]
    .filter((element) => Number(element.dataset.overlayOrder) >= 0)
    .sort(
      (left, right) => Number(right.dataset.overlayOrder) - Number(left.dataset.overlayOrder),
    )[0]
  if (
    event.key === 'Escape' &&
    topLayer === floating.value &&
    document.activeElement instanceof HTMLElement &&
    floating.value?.contains(document.activeElement)
  ) {
    restoreTriggerFocus()
  }
}

onMounted(() => window.addEventListener('keydown', handleEscape, true))
onUnmounted(() => window.removeEventListener('keydown', handleEscape, true))
</script>

<template>
  <HeadlessPopover
    v-slot="{ open, close }"
    as="div"
    class="ui-popover font-sans"
    :class="fluid ? 'flex min-w-0 flex-1' : 'inline-flex'"
  >
    <span
      ref="reference"
      class="ui-popover__trigger"
      :class="fluid ? 'flex min-w-0 flex-1' : 'inline-flex'"
      @mouseenter="onHoverEnter"
      @mouseleave="onHoverLeave"
    >
      <PopoverButton
        :class="[
          TRIGGER_BASE_CLASS,
          fluid ? 'flex min-w-0 flex-1' : 'inline-flex',
          triggerClass(open || hoverOpen),
        ]"
        :aria-label="triggerLabel"
        :disabled="disabled"
      >
        <slot name="trigger" :open="open || hoverOpen">{{ triggerLabel }}</slot>
      </PopoverButton>
    </span>

    <OverlayPortal>
      <FloatingLayer
        v-if="open || hoverOpen"
        v-model:element="floating"
        kind="popover"
        class="ui-popover__floating min-w-56"
        :style="floatingStyles"
        @mouseenter="onHoverEnter"
        @mouseleave="onHoverLeave"
      >
        <PopoverPanel
          static
          class="ui-popover__panel rounded-md border border-border bg-surface font-sans text-fg shadow-pop outline-none"
          :class="
            panelVariant === 'plain'
              ? 'ui-popover__panel--plain rounded-none border-0 bg-transparent p-0 shadow-none'
              : ''
          "
          role="dialog"
          :aria-label="panelLabel"
        >
          <!-- A pure hover reveal (mouse-only, no click) must not steal focus the way a real
               click-open should. -->
          <FocusTrap
            :features="open ? popoverFocusFeatures : 0"
            :containers="descendantFocusContainers"
          >
            <div class="ui-popover__content contents">
              <slot :close="() => closeFromContent(close)" />
            </div>
          </FocusTrap>
        </PopoverPanel>
      </FloatingLayer>
    </OverlayPortal>
  </HeadlessPopover>
</template>
